<script setup lang="ts">
import { useSnackbarStore } from '@/store/snackbar'
import VuePdfEmbed from 'vue-pdf-embed'
import { $signAnnotationCreate, $signAnnotationDelete, $signAnnotationUpdate } from '@/api/signs'
import ability from '@/plugins/casl/ability'
import { getPreferredPdfAiLink } from '@/helpers/utils/dropboxLinks'
import { $getS3FileDownloadUrls } from '@/api/fileManagement'

interface IProps {
  sign: any,
  annotations: any[],
  artworkFileCurrentPage: number,
  pdfScale: number,
  headerHeight: number,
}

const props = withDefaults(defineProps<IProps>(), {
  sign: null,
  annotations: () => ([]),
  artworkFileCurrentPage: 1,
  pdfScale: 100,
  headerHeight: 48.5,
})
interface Emit {
  (e: 'update:annotations', value: any): void
  (e: 'update:artworkFileCurrentPage', value: number): void
  (e: 'getAnnotations'): void
  (e: 'onSetHover', value: any): void
}

const emit = defineEmits<Emit>()

const snackbarStore = useSnackbarStore()

interface MapPosition { left: number; top: number; x: number; y: number; }

const isMoving = ref<boolean>(false)
const artworkFileLoading = ref<boolean>(true)
const artworkFilePdf = ref<InstanceType<typeof VuePdfEmbed> | null>(null)
const artworkFilePageCount = ref<number>(1)
const artworkFileMapPos = ref<MapPosition>({ left: 0, top: 0, x: 0, y: 0 })
const artworkS3PreviewBlobUrl = ref<string | null>(null)
const artworkS3PreviewLoading = ref<boolean>(false)
let latestArtworkS3PreviewRequestId = 0

const prepressProofFileCurrentPage = ref<number>(1)
const prepressProofFileLoading = ref<boolean>(true)
const prepressProofFilePdf = ref<InstanceType<typeof VuePdfEmbed> | null>(null)
const prepressProofFilePageCount = ref<number>(1)
const prepressProofFileMapPos = ref<MapPosition>({ left: 0, top: 0, x: 0, y: 0 })
const prepressS3PreviewBlobUrl = ref<string | null>(null)
const prepressS3PreviewLoading = ref<boolean>(false)
let latestPrepressS3PreviewRequestId = 0

const isCanAddAnnotation = computed<boolean>(() => {
  return ability.can('add', 'signartworkannot') && props.sign.is_active_campaign
})

const isCanChangeAnnotation = computed<boolean>(() => {
  return ability.can('change', 'signartworkannot') && props.sign.is_active_campaign
})

const isCanDeleteAnnotation = computed<boolean>(() => {
  return ability.can('delete', 'signartworkannot') && props.sign.is_active_campaign
})

const isCanResolveAnnotation = computed<boolean>(() => {
  return ability.can('resolve', 'signartworkannot') && props.sign.is_active_campaign
})

const artworkFileUrl = computed(() => {
  const artworkLinks = Array.isArray(props.sign?.artwork_file_links)
    ? props.sign.artwork_file_links.map((item: any) => item?.url).filter((url: any): url is string => !!url)
    : []
  return getPreferredPdfAiLink(artworkLinks)
})

const prepressProofFileUrl = computed(() => {
  const prepressLinks = Array.isArray(props.sign?.prepress_proof_file_links)
    ? props.sign.prepress_proof_file_links.map((item: any) => item?.url).filter((url: any): url is string => !!url)
    : []
  return getPreferredPdfAiLink(prepressLinks)
})

watch(artworkFileUrl, url => {
  artworkFileLoading.value = !!url
}, { immediate: true })

watch(prepressProofFileUrl, url => {
  prepressProofFileLoading.value = !!url
}, { immediate: true })

watch([artworkFileUrl, artworkS3PreviewLoading], ([url, isLoading]) => {
  artworkFileLoading.value = isLoading || !!url
}, { immediate: true })

watch([prepressProofFileUrl, prepressS3PreviewLoading], ([url, isLoading]) => {
  prepressProofFileLoading.value = isLoading || !!url
}, { immediate: true })

// const revokeArtworkS3PreviewBlob = () => {
//   if (artworkS3PreviewBlobUrl.value) {
//     URL.revokeObjectURL(artworkS3PreviewBlobUrl.value)
//     artworkS3PreviewBlobUrl.value = null
//   }
// }
//
// const revokePrepressS3PreviewBlob = () => {
//   if (prepressS3PreviewBlobUrl.value) {
//     URL.revokeObjectURL(prepressS3PreviewBlobUrl.value)
//     prepressS3PreviewBlobUrl.value = null
//   }
// }

// const fetchPreviewBlobUrl = async (url: string): Promise<string> => {
//   const response = await fetch(url)
//   if (!response.ok) {
//     throw new Error(`Failed to fetch preview file: HTTP ${response.status}`)
//   }
//   const blob = await response.blob()
//   return URL.createObjectURL(blob)
// }

// watch(
//   () => [isArtworkInS3.value, props.sign?.campaign, props.sign?.main_full_code || props.sign?.short_name],
//   async ([isInS3, projectId, code]) => {
//     const requestId = ++latestArtworkS3PreviewRequestId
//     revokeArtworkS3PreviewBlob()
//
//     if (!isInS3 || !projectId || !code) {
//       artworkS3PreviewLoading.value = false
//       return
//     }
//
//     artworkS3PreviewLoading.value = true
//     try {
//       const response = await $getS3FileDownloadUrls(projectId as number | string, {
//         folder: 'ARTWORK',
//         code: code as string,
//         intent: 'preview',
//       })
//       if (requestId !== latestArtworkS3PreviewRequestId) return
//
//       const items = Array.isArray(response?.results) ? response.results : [response]
//       const links = items.filter((item: any) => typeof item?.download_url === 'string' && !!item.download_url)
//       const preferredPdf = links.find((item: any) => /\.pdf$/i.test(String(item?.key || '')))
//       const previewUrl = preferredPdf?.download_url || links[0]?.download_url || null
//       if (!previewUrl) throw new Error('No S3 artwork preview URL found')
//
//       const previewBlobUrl = await fetchPreviewBlobUrl(previewUrl)
//       if (requestId !== latestArtworkS3PreviewRequestId) return
//       artworkS3PreviewBlobUrl.value = previewBlobUrl
//     } catch (error) {
//       if (requestId !== latestArtworkS3PreviewRequestId) return
//       revokeArtworkS3PreviewBlob()
//       console.error('Failed to fetch S3 artwork preview blob:', error)
//     } finally {
//       if (requestId === latestArtworkS3PreviewRequestId) {
//         artworkS3PreviewLoading.value = false
//       }
//     }
//   },
//   { immediate: true },
// )

// watch(
//   () => [isPrepressInS3.value, props.sign?.campaign, props.sign?.main_full_code || props.sign?.short_name],
//   async ([isInS3, projectId, code]) => {
//     const requestId = ++latestPrepressS3PreviewRequestId
//     revokePrepressS3PreviewBlob()
//
//     if (!isInS3 || !projectId || !code) {
//       prepressS3PreviewLoading.value = false
//       return
//     }
//
//     prepressS3PreviewLoading.value = true
//     try {
//       const response = await $getS3FileDownloadUrls(projectId as number | string, {
//         folder: 'PREPRESS_PROOF',
//         code: code as string,
//         intent: 'preview',
//       })
//       if (requestId !== latestPrepressS3PreviewRequestId) return
//
//       const items = Array.isArray(response?.results) ? response.results : [response]
//       const links = items.filter((item: any) => typeof item?.download_url === 'string' && !!item.download_url)
//       const preferredPdf = links.find((item: any) => /\.pdf$/i.test(String(item?.key || '')))
//       const previewUrl = preferredPdf?.download_url || links[0]?.download_url || null
//       if (!previewUrl) throw new Error('No S3 prepress preview URL found')
//
//       const previewBlobUrl = await fetchPreviewBlobUrl(previewUrl)
//       if (requestId !== latestPrepressS3PreviewRequestId) return
//       prepressS3PreviewBlobUrl.value = previewBlobUrl
//     } catch (error) {
//       if (requestId !== latestPrepressS3PreviewRequestId) return
//       revokePrepressS3PreviewBlob()
//       console.error('Failed to fetch S3 prepress preview blob:', error)
//     } finally {
//       if (requestId === latestPrepressS3PreviewRequestId) {
//         prepressS3PreviewLoading.value = false
//       }
//     }
//   },
//   { immediate: true },
// )

// onBeforeUnmount(() => {
//   latestArtworkS3PreviewRequestId += 1
//   latestPrepressS3PreviewRequestId += 1
//   revokeArtworkS3PreviewBlob()
//   revokePrepressS3PreviewBlob()
// })

const isShownAnyAnnotationMenu = computed(() => {
  return !!props.annotations
    .filter(annot => annot.page === props.artworkFileCurrentPage && !!annot.isShownAnnotationMenu).length
})

// If prepress_proof_link provided - show all files (Artwork and Prepress Proof)
const isShowAllFiles = computed(() => {
  return !!(props.sign?.prepress_proof_file || props.sign?.prepress_proof_file_links?.length)
})

const addAnnotation = (newAnnotation: any) => {
  const updatedAnnotations = [...props.annotations, newAnnotation]
  emit('update:annotations', updatedAnnotations)
}

const hideAnnotationMenus = () => {
  emit('update:annotations', props.annotations.map(i => ({ ...i, isShownAnnotationMenu: false }) ))
}

const createAnnotation = async (event: MouseEvent) => {
  if (!isCanAddAnnotation.value) return

  if (!props?.sign?.id) return

  const textLayer = document.getElementsByClassName('textLayer')[0] as HTMLElement

  if (
    event.target === textLayer
    && !(event.target as HTMLElement).hasAttribute('annot')
    && !isShownAnyAnnotationMenu.value
  ) {
    // Calculate percentages
    const topPerc = ((event.offsetY - 12) / (textLayer?.offsetHeight || 100)) * 100
    const leftPerc = ((event.offsetX - 12) / (textLayer?.offsetWidth || 100)) * 100

    const payload = {
      sign: props.sign.id,
      top: topPerc,
      left: leftPerc,
      page: props.artworkFileCurrentPage
    }
    try {
      const res = await $signAnnotationCreate(payload)
      addAnnotation(res.data)
    } catch (e) {
      console.log(e)
    }
  } else if (isShownAnyAnnotationMenu.value) {
    hideAnnotationMenus()
  }
}

const updateAnnotation = async (annotation: any) => {
  try {
    await $signAnnotationUpdate(annotation.id, annotation)
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation saved successfully!'
    })
  } catch (e) {
    console.log(e)
  }
}

const deleteAnnotation = async (id: number) => {
  try {
    await $signAnnotationDelete(id)
    emit('getAnnotations')
    hideAnnotationMenus()
    snackbarStore.showMessage({
      color: 'success',
      icon: 'mdi-checkbox-marked-circle',
      title: 'Success',
      text: 'Annotation deleted successfully!'
    })
  } catch (e) {
    console.log(e)
  }
}

const onMoveMapStart = (e: MouseEvent, mapPos: MapPosition, elementId: string) => {
  // On mouse left button (primary) - move PDF
  if (e.button === 0) {
    const el = document.getElementById(elementId) as HTMLElement
    isMoving.value = true
    mapPos.left = el.scrollLeft
    mapPos.top = el.scrollTop
    mapPos.x = e.clientX
    mapPos.y = e.clientY
  }
}

const onMoveMap = (e: MouseEvent, isCenter: boolean, mapPos: MapPosition, elementId: string) => {
  if (isCenter) {
    setTimeout(() => {
      const containerIds = ['artwork-file-pdf-container-id']
      containerIds.forEach((containerId: string) => {
        const container = document.getElementById(containerId) as HTMLElement
        container.scrollTop = container.scrollHeight / 2 - container.clientHeight / 2
        container.scrollLeft = container.scrollWidth / 2 - container.clientWidth / 2
      })
    }, 0)
  } else {
    const el = document.getElementById(elementId) as HTMLElement
    const dx = e.clientX - mapPos.x
    const dy = e.clientY - mapPos.y

    el.scrollTop = mapPos.top - dy
    el.scrollLeft = mapPos.left - dx
  }
}

</script>

<template>
  <VRow
    ref="artworksBlock"
    class="artwork-row mx-0"
    :style="{ '--header-height': `${headerHeight}px` }"
  >
    <VCol
      class="position-relative py-0 px-1"
      :class="{ 'min-height-200': artworkFileLoading }"
    >
      <div
        v-if="artworkFileUrl"
        style="margin: auto; max-height: inherit"
      >
        <VOverlay
          :model-value="artworkFileLoading || artworkS3PreviewLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <div class="text-center">
          <span
            v-if="isShowAllFiles"
            class="mr-1"
          >
            Artwork
          </span>
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="artworkFileCurrentPage === 1"
            @click="emit('update:artworkFileCurrentPage', artworkFileCurrentPage - 1)"
          >
            <VIcon icon="mdi-arrow-left-bold-circle-outline" />
          </VBtn>
          <span>
            {{ artworkFileCurrentPage }}
          </span>
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="artworkFileCurrentPage === artworkFilePageCount"
            @click="emit('update:artworkFileCurrentPage', artworkFileCurrentPage + 1)"
          >
            <VIcon icon="mdi-arrow-right-bold-circle-outline" />
          </VBtn>
        </div>
        <div
          id="artwork-file-pdf-container-id"
          class="artwork-file-pdf-container"
          @mousedown="onMoveMapStart($event, artworkFileMapPos, 'artwork-file-pdf-container-id')"
          v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, artworkFileMapPos, 'artwork-file-pdf-container-id') } }"
          @mouseup="isMoving = false"
          @mouseout="isMoving = false"
        >
          <div
            class="artwork-file-pdf-wrapper overflow-hidden"
            :style="{ width: `${pdfScale}%` }"
            @click.right.prevent="(event) => { createAnnotation(event) }"
          >
            <VuePdfEmbed
              v-if="artworkFileUrl"
              ref="artworkFilePdf"
              id="artwork-file-pdf"
              textLayer
              :source="artworkFileUrl"
              :page="artworkFileCurrentPage"
              @rendered="artworkFileLoading = false; artworkFilePageCount = artworkFilePdf.pageCount"
            />
            <div
              v-for="(annotation, key) in annotations.filter(annot => annot.page === artworkFileCurrentPage)"
              :id="`annotation${key}`"
              :key="key"
              class="pdfAnnotation"
              :class="{ 'hovered': annotation.hover, 'd-none': artworkFileLoading }"
              :style="{ left: annotation.left + '%', top: annotation.top + '%' }"
              annot="true"
              @mouseover="emit('onSetHover', { annotation: annotation, status: true })"
              @mouseout="emit('onSetHover', { annotation: annotation, status: false })"
            >
              <span annot="true">
                {{ annotation.index }}
              </span>
              <VMenu
                v-model="annotation.isShownAnnotationMenu"
                :id="'pop' + annotation.id"
                :activator="`#annotation${key}`"
                :open-on-click="true"
                :open-on-hover="false"
                :close-on-content-click="false"
                location="top center"
                offset="5"
              >
                <VCard>
                  <VCardText
                    class="border rounded-lg pa-3"
                  >
                    <VTextarea
                      v-model="annotation.comment"
                      placeholder="Enter comments..."
                      class="mb-1"
                      rows="3"
                      :disabled="annotation.is_resolved || !isCanChangeAnnotation"
                    />
                    <div
                      v-if="!annotation.is_resolved"
                      class="mt-1 d-flex"
                    >
                      <div
                        v-if="isCanChangeAnnotation"
                        class="save-btn"
                      >
                        <VBtn
                          color="success"
                          :disabled="!annotation.comment"
                          @click="updateAnnotation(annotation)"
                        >
                          Save
                        </VBtn>
                      </div>
                      <VTooltip
                        v-if="!annotation.comment"
                        activator=".save-btn"
                        location="top center"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span>
                          Please, add comment
                        </span>
                      </VTooltip>
                      <div
                        v-if="isCanResolveAnnotation"
                        class="set-resolved-btn">
                        <VBtn
                          class="ml-1"
                          color="info"
                          :disabled="!annotation.comment"
                          @click="annotation.is_resolved=true; updateAnnotation(annotation)"
                        >
                          Set Resolved
                        </VBtn>
                      </div>
                      <VTooltip
                        v-if="!annotation.comment"
                        activator=".set-resolved-btn"
                        location="top center"
                        offset="5"
                        class="default-tooltip"
                      >
                        <span>
                          Please, add comment
                        </span>
                      </VTooltip>
                      <VBtn
                        v-if="isCanDeleteAnnotation"
                        class="ml-1"
                        @click="deleteAnnotation(annotation.id)"
                      >
                        Delete
                      </VBtn>
                    </div>
                  </VCardText>
                </VCard>
              </VMenu>
            </div>
          </div>
        </div>
        <div
          v-if="!artworkFileUrl && !(artworkFileLoading || artworkS3PreviewLoading)"
          class="pa-2 text-center"
        >
          Failed to load PDF preview
        </div>
      </div>
      <div
        v-else-if="!artworkFileUrl"
        style="margin: auto;"
      >
        <h4>File not provided</h4>
      </div>
    </VCol>
    <VCol
      v-if="isShowAllFiles"
      class="position-relative py-0 px-1"
      :class="{ 'min-height-200': prepressProofFileLoading }"
    >
      <div
        v-if="prepressProofFileUrl"
        style="margin: auto; max-height: inherit"
      >
        <VOverlay
          :model-value="prepressProofFileLoading || prepressS3PreviewLoading"
          persistent
          absolute
          contained
          class="align-center justify-center"
        >
          <VProgressCircular
            indeterminate
            color="primary"
          />
        </VOverlay>
        <div class="text-center">
          <span
            v-if="isShowAllFiles"
            class="mr-1"
          >
            Prepress Proof
          </span>
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="prepressProofFileCurrentPage === 1"
            @click="prepressProofFileCurrentPage -= 1"
          >
            <VIcon icon="mdi-arrow-left-bold-circle-outline" />
          </VBtn>
          <span>
            {{ prepressProofFileCurrentPage }}
          </span>
          <VBtn
            size="sm"
            class="p-0 rounded-circle"
            pill
            color="light"
            :disabled="prepressProofFileCurrentPage === prepressProofFilePageCount"
            @click="prepressProofFileCurrentPage += 1"
          >
            <VIcon icon="mdi-arrow-right-bold-circle-outline" />
          </VBtn>
        </div>
        <div
          id="prepress-proof-file-pdf-container-id"
          class="prepress-proof-file-pdf-container"
          @mousedown="onMoveMapStart($event, prepressProofFileMapPos, 'prepress-proof-file-pdf-container-id')"
          v-on="{ mousemove: (event) => { if (isMoving) onMoveMap(event, false, prepressProofFileMapPos, 'prepress-proof-file-pdf-container-id') } }"
          @mouseup="isMoving = false"
          @mouseout="isMoving = false"
        >
          <div
            class="prepress-proof-file-pdf-wrapper overflow-hidden"
            :style="{ width: `${pdfScale}%` }"
          >
            <VuePdfEmbed
              v-if="prepressProofFileUrl"
              ref="prepressProofFilePdf"
              id="prepress-proof-file-pdf"
              textLayer
              :source="prepressProofFileUrl"
              :page="prepressProofFileCurrentPage"
              @rendered="prepressProofFileLoading = false; prepressProofFilePageCount = prepressProofFilePdf.pageCount"
            />
          </div>
        </div>
        <div
          v-if="!prepressProofFileUrl && !(prepressProofFileLoading || prepressS3PreviewLoading)"
          class="pa-2 text-center"
        >
          Failed to load PDF preview
        </div>
      </div>
      <div
        v-else-if="!prepressProofFileUrl"
        style="margin: auto;"
      >
        <h4>Prepress Proof File not provided</h4>
      </div>
    </VCol>
  </VRow>
</template>

<style lang="scss">
.artwork-file-pdf-container, .prepress-proof-file-pdf-container {
  overflow: scroll;
  max-width: 100%;
  // max-height: display size - header height - top and bottom dialog padding - artwork file pagination height
  max-height: calc(100vh - var(--header-height) - 40px - 22.5px);
  .artwork-file-pdf-wrapper, .prepress-proof-file-pdf-wrapper {
    position: relative;
    margin: 0 auto;
    &:active {
      cursor: all-scroll;
    }
    .pdfAnnotation {
      position: absolute;
      display: block;
      width: 24px;
      height: 24px;
      background: #dd082b;
      border-radius: 50%;
      text-align: center;
      color: #fff;
      cursor: pointer;
      &.hovered {
        background: #fff;
        color: #dd082b;
        border: 1px solid #dd082b;
      }
    }
  }
}

canvas {
  width: 100% !important;
  height: unset !important;
}

.artwork-row {
  max-height: inherit;
  position: relative;
  > .v-col {
    max-height: inherit;
  }
}

.sign-artwork-review-pdf-pagination-block {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(0, -50%);
}
</style>
